import { type DatePickerProps, DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { pickersCalendarHeaderClasses } from "@mui/x-date-pickers/PickersCalendarHeader";
import { pickerDayClasses } from "@mui/x-date-pickers/PickerDay";
import { pickersInputBaseClasses, pickersOutlinedInputClasses } from "@mui/x-date-pickers/PickersTextField";
import { inputAdornmentClasses } from "@mui/material/InputAdornment";
import { buttonBaseClasses } from "@mui/material/ButtonBase";
import { Stack } from "@mui/material";
import { CalendarDaysIcon, XIcon } from "./icons";
import { yearCalendarClasses } from "@mui/x-date-pickers/YearCalendar";
import { pickersSectionListClasses } from "@mui/x-date-pickers/PickersSectionList";

export const DatePicker = ({ hideStartAdornment, ...props }: DatePickerProps & { hideStartAdornment?: boolean }) => {
  const consumerTextField = props.slotProps?.textField;
  const startAdornment = hideStartAdornment ? null : (
    <Stack sx={{ alignItems: "center", mr: 1, pb: 0.45 }}>
      <CalendarDaysIcon
        size={16}
        color={theme => (props.value ? theme.palette.text.primary : theme.palette.text.placeholder)}
      />
    </Stack>
  );

  return (
    <MuiDatePicker
      {...props}
      reduceAnimations
      sx={{
        pointerEvents: props.disabled ? "none" : "auto",
        [`& .${pickersInputBaseClasses.root}`]: {
          "cursor": "pointer",
          "borderRadius": 1,
          [`& .${pickersSectionListClasses.root}`]: {
            py: "6.5px",
            color: !props.value ? "transparent" : undefined,
          },
          [`& .${pickersInputBaseClasses.sectionContent}`]: { fontSize: theme => theme.typography.pxToRem(14) },
          [`& .${inputAdornmentClasses.root}`]: { marginLeft: 0, marginRight: 0 },
          [`& .${buttonBaseClasses.root}`]: { color: theme => `${theme.palette.text.primary} !important` },
          "&.Mui-disabled": { color: theme => `${theme.palette.text.primary} !important` },
          "fieldset": { border: theme => `1px solid ${theme.palette.grey["400"]}` },
          [`&.Mui-focused .${pickersOutlinedInputClasses.notchedOutline}`]: {
            border: theme => `2px solid ${theme.palette.primary["500"]}`,
          },
          [`&:hover:not(.Mui-disabled) .${pickersOutlinedInputClasses.notchedOutline}`]: {
            border: theme => `1px solid ${theme.palette.primary["500"]}`,
          },
          ...props.sx,
        },
      }}
      slots={{
        clearIcon: props => <XIcon {...props} size={14} color={theme => theme.palette.text.primary} />,
        openPickerButton: () => null,
      }}
      slotProps={{
        layout: {
          sx: theme => ({
            mt: 0.5,
            border: `1px solid ${theme.palette.grey["300"]}`,
            borderRadius: 1,
            [`.${pickersCalendarHeaderClasses.label}`]: { textTransform: "capitalize" },
            [`.${pickerDayClasses.root}, && .${yearCalendarClasses.button}`]: {
              "&:hover, &:focus": { bgcolor: theme => `${theme.palette.action.hover} !important` },
              "&.Mui-selected": {
                bgcolor: theme => `${theme.palette.action.selected} !important`,
                fontWeight: 600,
                color: theme => `${theme.palette.text.primary} !important`,
              },
            },
            [`.${pickerDayClasses.today}, .${yearCalendarClasses.button}[aria-current="date"]`]: {
              border: theme => `1px solid ${theme.palette.grey["400"]} !important`,
              bgcolor: "transparent !important",
            },
            [`.${pickerDayClasses.disabled}, .${yearCalendarClasses.button}[disabled]`]: {
              color: theme => `${theme.palette.action.disabled} !important`,
            },
          }),
        },
        ...props.slotProps,
        textField: ownerState => {
          const base =
            typeof consumerTextField === "function" ? consumerTextField(ownerState) : (consumerTextField ?? {});
          return {
            ...base,
            slotProps: {
              ...base.slotProps,
              input: {
                ...base.slotProps?.input,
                startAdornment: base.slotProps?.input?.startAdornment ?? startAdornment,
              },
            },
          };
        },
      }}
    />
  );
};
